import type { ImageFileMetadata, ImageMedia } from '@carboncopyco/types';
import {
  getBase64Image,
  getFileName,
  getImageExtension,
  loadUrlToImage,
} from '@carboncopyco/utils';
import { ATTRIBUTES } from './constants';
import { getFetch } from './globals';
import { camelToKebab } from './string';

export const getImageMetadata = async (
  url: string
): Promise<ImageFileMetadata> => {
  const { naturalHeight, naturalWidth } = await loadUrlToImage(url);
  const format = await getImageFormat(url);
  const name = getImageName(url);
  return {
    name,
    format,
    width: naturalWidth,
    height: naturalHeight,
  };
};

export const getImageObjectPositionSetting = (target: HTMLElement) => {
  const isBackgroundImage = !!target.style.backgroundImage;
  return isBackgroundImage ? 'backgroundPosition' : 'objectPosition';
};

export const getImageObjectFitSetting = (target: HTMLElement) => {
  const isBackgroundImage = !!target.style.backgroundImage;
  return isBackgroundImage ? 'backgroundSize' : 'objectFit';
};

export const getImageFormat = async (url: string): Promise<string> => {
  let format;

  try {
    const { pathname } = new URL(url);
    format = getImageExtension(pathname);
  } catch {
    format = getImageExtension(url);
  }

  if (!format) {
    const base64 = await getBase64OfUrl(url);
    format = getImageExtension(base64);
  }
  return format;
};

const getImageName = (url: string) => {
  try {
    const { pathname } = new URL(url);
    const fullName = getFileName(pathname);
    const dotIndex = fullName.lastIndexOf('.');
    return fullName.slice(0, dotIndex);
  } catch {
    return '';
  }
};

const getBase64OfUrl = async (
  imageUrl: string
): Promise<string | undefined> => {
  const timestamp = new Date().getTime();
  try {
    const url = new URL(imageUrl);
    // cache busting is required for images that are missing format (jpg, png, etc.) in their url, and had a related F&R rule.
    // otherwise, we may experience CORS error
    url.searchParams.set(
      'demostack-cache-busting-timestamp',
      String(timestamp)
    );
    const fetch = getFetch();
    const response = await fetch(url.toString(), {
      credentials: 'include',
    });

    const blob = await response.blob();

    return new Promise((res, rej) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        res(reader.result as string);
      };
      reader.onerror = rej;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to fetch image', error);
  }
};

export const convertImageUrlToBase64 = async (url: string) => {
  // convert image url to base64 without cross-origin limitation
  const base64 = await getBase64OfUrl(url);

  if (!base64) {
    return '';
  }

  // load the base64 (could type image/png, image/jpg, image/svg+xml) to a new image element
  const image = await loadUrlToImage(base64);
  // convert the image to base64 again, so the final format will be always PNG - for file service support (we can't upload base64 of svg)
  return getBase64Image(image);
};

const imageSettingsSizeDictionary = {
  fit: 'contain',
  fill: 'cover',
  crop: 'fill',
  tile: 'fill',
};

const imageSettingsPositionDictionary = (value: string) =>
  value.replace('-', ' ');

export const setImageSettingsToElement = (
  element: HTMLElement,
  settings: {
    sizing?: ImageMedia['sizing'];
    position?: ImageMedia['position'];
  }
) => {
  if (settings?.sizing)
    element.style.setProperty(
      camelToKebab(getImageObjectFitSetting(element)),
      imageSettingsSizeDictionary[settings.sizing],
      'important'
    );
  if (settings?.position) {
    element.style.setProperty(
      camelToKebab(getImageObjectPositionSetting(element)),
      imageSettingsPositionDictionary(settings.position),
      'important'
    );
  }
  element.setAttribute(ATTRIBUTES.DATA_DS_STYLE_EDIT_ID, 'true');
};
